import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { supabase } from '../../lib/supabase';
import { readEdgeFunctionError } from '../../lib/edgeError';
import type { PixData } from './subscription';

// Cobrança única (R$199,90) de um artista JÁ EXISTENTE (criado no diagnóstico).
// A confirmação apenas DESBLOQUEIA o perfil (artists.is_locked → false).

export interface ArtistPurchaseEntry {
  status: 'idle' | 'pending' | 'received' | 'failed';
  pixData: PixData | null;
  artistId: string | null;
  loading: boolean;
  error: string | null;
}

export interface ArtistPurchasesState {
  byPurchase: Record<string, ArtistPurchaseEntry>;
}

const initialState: ArtistPurchasesState = { byPurchase: {} };

const emptyEntry = (): ArtistPurchaseEntry => ({
  status: 'idle',
  pixData: null,
  artistId: null,
  loading: false,
  error: null,
});

// ─── Thunks ───────────────────────────────────────────────────────────────────

/**
 * Cria a cobrança única de um perfil JÁ EXISTENTE (PIX ou cartão).
 * Cartão aprovado na hora já retorna `status: 'received'` + `artistId`; PIX
 * retorna `pixData` e o perfil é desbloqueado quando o webhook confirmar.
 */
export const createArtistCharge = createAsyncThunk(
  'artistPurchases/createCharge',
  async (
    payload: {
      artistId: string;
      customerId: string;
      billingType?: 'PIX' | 'CREDIT_CARD' | 'DEBIT_CARD';
      // Parcelamento (somente cartão de CRÉDITO); 1 ou ausente = à vista.
      installmentCount?: number;
      couponCode?: string;
      creditCard?: {
        holderName: string;
        number: string;
        expiryMonth: string;
        expiryYear: string;
        ccv: string;
      };
      creditCardHolderInfo?: {
        name: string;
        email?: string;
        cpfCnpj: string;
        postalCode: string;
        addressNumber?: string;
        address?: string;
        province?: string;
        city?: string;
        uf?: string;
        phone: string;
      };
    },
    { rejectWithValue }
  ) => {
    const { data, error } = await supabase.functions.invoke('asaas-create-artist-charge', {
      body: payload,
    });
    if (error) {
      return rejectWithValue({ message: await readEdgeFunctionError(error, 'Erro ao criar a cobrança do perfil') });
    }
    const d = data as { purchaseId: string; artistId?: string; status?: string; pixData?: PixData | null };
    return {
      purchaseId: d.purchaseId,
      artistId: d.artistId ?? null,
      status: d.status,
      pixData: d.pixData ?? null,
    };
  }
);

// A cada 4 voltas do poll (~20s) confirma direto na Asaas em vez de só reler a tabela.
// A tabela local só muda quando o webhook chega; se ele estiver mudo (fila pausada, como
// em 08/08/2026), o poll giraria os 10 min inteiros com o pagamento já compensado.
const RECONCILE_EVERY = 4;

/**
 * Aguarda a confirmação do pagamento. Duas fontes: a tabela local (atualizada pelo webhook)
 * e, periodicamente, a própria Asaas via asaas-reconcile-purchase — que também desbloqueia
 * o perfil. Poll resiliente: 5s, até 10min, tolerando erros isolados. Retorna o artistId.
 */
export const pollArtistPurchase = createAsyncThunk(
  'artistPurchases/poll',
  async (payload: { purchaseId: string }, { rejectWithValue, signal }) => {
    const { purchaseId } = payload;
    const intervalMs = 5000;
    const maxMs = 10 * 60 * 1000;
    const start = Date.now();
    let consecutiveErrors = 0;
    let round = 0;

    while (Date.now() - start < maxMs) {
      if (signal.aborted) return rejectWithValue({ purchaseId, message: 'Cancelado' });

      const { data, error } = await supabase
        .from('artist_purchases')
        .select('status, artist_id')
        .eq('id', purchaseId)
        .maybeSingle();

      if (error) {
        consecutiveErrors += 1;
        if (consecutiveErrors >= 3) {
          return rejectWithValue({ purchaseId, message: 'Falha ao verificar o pagamento.' });
        }
      } else {
        consecutiveErrors = 0;
        if (data && data.status === 'received' && data.artist_id) {
          return { purchaseId, artistId: data.artist_id as string };
        }
        if (data && data.status === 'failed') {
          return rejectWithValue({ purchaseId, message: 'O pagamento não foi confirmado.' });
        }
      }

      round += 1;
      if (round % RECONCILE_EVERY === 0) {
        // Falha aqui (Asaas fora, rede) não interrompe o poll: segue pela tabela local.
        try {
          const { data: rec } = await supabase.functions.invoke('asaas-reconcile-purchase', {
            body: { purchaseId },
          });
          if (rec?.status === 'received' && rec?.artistId) {
            return { purchaseId, artistId: rec.artistId as string };
          }
        } catch {
          /* ignora — próxima volta tenta de novo */
        }
      }

      await new Promise((r) => setTimeout(r, intervalMs));
    }

    return rejectWithValue({ purchaseId, message: 'Tempo de confirmação excedido.' });
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const ensure = (state: ArtistPurchasesState, id: string): ArtistPurchaseEntry => {
  if (!state.byPurchase[id]) state.byPurchase[id] = emptyEntry();
  return state.byPurchase[id];
};

const artistPurchasesSlice = createSlice({
  name: 'artistPurchases',
  initialState,
  reducers: {
    resetArtistPurchase(state, action: { payload: string }) {
      delete state.byPurchase[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createArtistCharge.pending, (state) => {
        // Sem purchaseId ainda; o erro/loading inicial vive no componente.
        void state;
      })
      .addCase(createArtistCharge.fulfilled, (state, action) => {
        const { purchaseId, artistId, status, pixData } = action.payload;
        const e = ensure(state, purchaseId);
        e.loading = false;
        e.pixData = pixData;
        e.artistId = artistId;
        e.status = status === 'received' ? 'received' : 'pending';
      })
      .addCase(pollArtistPurchase.fulfilled, (state, action) => {
        const e = ensure(state, action.payload.purchaseId);
        e.status = 'received';
        e.artistId = action.payload.artistId;
        e.pixData = null;
      })
      .addCase(pollArtistPurchase.rejected, (state, action) => {
        const payload = action.payload as { purchaseId: string; message: string } | undefined;
        if (!payload) return;
        const e = ensure(state, payload.purchaseId);
        e.error = payload.message;
        if (/não foi confirmado/.test(payload.message)) e.status = 'failed';
      });
  },
});

export const { resetArtistPurchase } = artistPurchasesSlice.actions;
export default artistPurchasesSlice.reducer;
